import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { advHas } from '@src/slide/infrastructure/module/independent/assert/get-set';
import StyleHandler from '@src/slide/domain/ui-model/adapter/style-handler';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import {
    BORDER_KEY,
    BorderType,
    IBorderInfo,
    IBorderProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { getAllChildModels, mergeNoConflictProperty } from '../utils/merge-property';
import UpdateAndRemoveLogic from '@src/slide/domain/ui-model/element-modeles/property/fill/update-remove';
import * as mobx from 'mobx';
import { copy } from '@src/slide/infrastructure/module/independent/object-operation';
import { isSketchStyle } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/sketch-style';
import SketchUpdateAndRemove from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/update-remove';

type IBorderModel = IElementBaseModel | ITableModel | IMultiplyModel;

export const defaultBorderColor = '#000000';

export default class Border implements IBorderProperty {
    private model: IBorderModel;
    // 是否只更新ui
    private onlyUIChange: boolean | undefined;
    // 是否只更新颜色ui
    private isPreviewColor: boolean | undefined;
    /**
     * 多选图形、组合的属性是综合所有子元素而来的，基于性能考虑，加入缓存，只在必要时重新获取更新缓存
     * @private
     */
    @mobx.observable private mergePropertyValCache: IBorderInfo | null | undefined ;

    public constructor(model: IBorderModel) {
        this.mergePropertyValCache = null;
        this.model = model;
    }

    public getBorderType = (): BorderType | undefined => {
        const borderSet = this.getBorderInfo();
        if (this.model.type === UIModelType.MULTIPLY) {
            if (!borderSet) {
                return undefined;
            }
            const childModels = getAllChildModels(this.model)
                .filter(child => child.type !== UIModelType.CHART && child.type !== UIModelType.TABLE);
            const firstChildBorder = (childModels[0] as IBorderModel)
                .getBorder?.().getBorderInfo() || {};
            const firstBorderPropsLength = Object.keys(firstChildBorder)
                .filter(p => ![null, undefined].includes(firstChildBorder[p])).length;
            const mergeBorderPropsLength = Object.keys(borderSet)
                .filter(p => ![null, undefined].includes(borderSet[p])).length;
            if (firstBorderPropsLength !== mergeBorderPropsLength) {
                return undefined;
            }
            const sketchChild = childModels
                .find(model => isSketchStyle(model.props.style.border?.sketchStyle?.type));
            if (sketchChild) {
                const sketchChildBorder = sketchChild.getBorder?.().getBorderInfo() || {};
                const sketchBorderPropsLength = Object.keys(sketchChildBorder)
                    .filter(p => ![null, undefined].includes(sketchChildBorder[p])).length;
                if (mergeBorderPropsLength !== sketchBorderPropsLength) {
                    return undefined;
                }
            }
        }
        if (borderSet?.gradFill) {
            return BorderType.GradualFill;
        }
        if (!borderSet?.color) {
            return BorderType.NoFill;
        }
        return BorderType.SolidFill;
    };

    public getBorderInfo = (): IBorderInfo | null | undefined => {
        if ([UIModelType.GROUP, UIModelType.MULTIPLY].includes(this.model.type as UIModelType)) {
            return this.mergePropertyValCache;
        }
        if (this.model.type === UIModelType.TABLE) {
            return (this.model as ITableModel).getBorder().getTableBorderInfo().borderColor;
        }
        if (!this.model.props) return null;
        const { style } = (this.model as IElementBaseModel).props;
        if (style.border) {
            Object.keys(style.border).forEach((key) => {
                advHas(style.border, key);
            });
        }
        return style.border;
    };

    public updateCurrentBorder(borderSet: IBorderInfo, onlyUIChange?: boolean, isPreviewColor?: boolean): void {
        this.onlyUIChange = onlyUIChange;
        this.isPreviewColor = isPreviewColor;
        if (onlyUIChange) {
            this.handleBorder(this.model, borderSet);
        } else {
            transaction(() => {
                this.handleBorder(this.model, borderSet);
            });
        }
    }

    public handleBorderSpecial(
        borderSet: IBorderInfo, curBorderSet = this.getBorderInfo(),
        borderType = this.getBorderType(),
    ): void {
        const isNoFill = borderType === BorderType.NoFill;
        if (
            isNoFill
            && (borderSet?.style || borderSet?.width)
            && (!borderSet.color && !borderSet.gradFill)
        ) { // 之前是无颜色，并且设置的不是颜色，给一个默认黑色
            borderSet.color = defaultBorderColor;
        } else {
            this.setBorderColor(borderSet, curBorderSet);
            this.setBorderStyleAndWidth(isNoFill, borderSet);
        }
    }

    /**
     * 刷新 多选图形|组合 合并后的效果值
     */
    public refreshMergePropertyValCache(): IBorderInfo | null | undefined  {
        if ([UIModelType.GROUP, UIModelType.MULTIPLY].includes(this.model.type as UIModelType)) {
            const mergeModel = this.model as (IGroupModel | IMultiplyModel);
            const getPropertyFunc = model => ({ [BORDER_KEY]: model.getBorder?.().getBorderInfo() });
            const uiModelPropertyObj = mergeNoConflictProperty(mergeModel, getPropertyFunc);
            this.mergePropertyValCache = uiModelPropertyObj[BORDER_KEY] || null;
        }
        return this.mergePropertyValCache;
    }

    /**
     * 设置borderSet的color(style-handler文件迁移而来)
     */
    private setBorderColor(borderSet: IBorderInfo, curBorderSet = this.getBorderInfo()) {
        if (!this.isPreviewColor && typeof borderSet?.color === 'undefined'
            && curBorderSet?.color === defaultBorderColor) {
            if (borderSet) {
                borderSet.color = curBorderSet.color;
            }
        }
    }

    /**
     * 设置border的style和width(style-handler文件迁移而来)
     */
    private setBorderStyleAndWidth(isNoFill: boolean, borderSet: IBorderInfo) {
        if (isNoFill && borderSet?.color && !(borderSet.style || borderSet.width)) {
            // 之前是无颜色，并且设置的是颜色,且无样式和宽度，则给一个默认值
            borderSet.style = 'solid';
            borderSet.width = ptToCanvas(1);
        }
    }

    /**
     * 针对不同的model，调用不同的更新接口进行更新
     * @param model
     * @param fillSet
     * @param onlyUIChange
     */
    private handleBorder = (model: IBorderModel, borderSet: IBorderInfo): void => {
        if (model.type === UIModelType.TABLE) {
            (model as ITableModel).getBorder().updateTableBorderInfo(borderSet);
        } else if (model.type === UIModelType.MULTIPLY) {
            const { selectedModels } = model as IMultiplyModel;
            selectedModels.forEach((model: IElementBaseModel) => {
                this.handleBorder(model, copy(borderSet, true));
            });
        } else if (model.type === UIModelType.GROUP) {
            ((model as IGroupModel).children as IElementBaseModel[]).forEach((model: IElementBaseModel) => {
                this.handleBorder(model, borderSet);
            });
        } else {
            this.updateModelBorder(model as IElementBaseModel, borderSet);
        }
    };

    /**
     * 更新model的border
     */
    private updateModelBorder(model: IElementBaseModel, borderSet: IBorderInfo) {
        this.handleBorderSpecial(
            borderSet, model.getBorder().getBorderInfo(),
            model.getBorder().getBorderType(),
        );
        const props = { style: { border: borderSet } };
        if (this.onlyUIChange) {
            StyleHandler.updateUiProps(props, model);
            model.previewChange += 1;
        } else {
            const updateAndRemove = UpdateAndRemoveLogic.convertToUpdateAndRemove(props, model.props);
            SketchUpdateAndRemove.updateSketchUpdateAndRemoveProps(model, props, updateAndRemove);
            if (updateAndRemove.remove) {
                model.updateAndRemoveProps(updateAndRemove.update, updateAndRemove.remove);
            } else {
                model.updateProps(updateAndRemove.update);
            }
        }
    }
}
